import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import type {
  CriterionGroupReading,
  CriterionReading,
  ExtractDrop,
  ObjectiveReading,
  TurnReading,
} from '@/lib/conversation-model';
import {
  criterionGroupMention,
  criterionTone,
  hasIgnoredThreshold,
  isVerdictReading,
  turnDefect,
} from '@/lib/conversation-model';
import {
  IGNORED_THRESHOLD_SENTENCE,
  passableLabel,
  scoreLabel,
  thresholdLabel,
  verdictLabel,
} from '@/lib/protocol-labels';
import { objectiveStatusLabel } from '@/lib/session-labels';
import type { SessionTurn } from '@matter_extractor/shared';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { CriterionCard, CriterionName } from './criterion-popover';
import { LayerLine, TurnLayerShell } from './layer-shell';
import {
  CRITERION_TONE_TEXT,
  type ConversationSources,
  FAREWELL_NOT_EVALUATED,
  GROUP_TONE_BORDER,
  STATUS_DOT,
  STATUS_TEXT,
} from './sources';

/**
 * The Objectifs layer of one turn (§12.3): the scribe's reading, judged.
 *
 * ## The score sits BESIDE its threshold, and a dash is not a zero
 *
 * « Le scribe ne connaît ni les seuils ni la quantité : il note, le code
 * compare » (§8.2), so the two figures are drawn together and the tick is the
 * code's answer rather than the model's. A criterion the scribe has not judged
 * shows « - » and never `0`: zero is a judgement, and a screen that printed one
 * where nobody judged would make a reader debug a score nobody gave.
 *
 * ## Four ways an extract stops counting, and each one stays on the page
 *
 * An extract INVALIDATED by a condition « reste dans le journal marqué
 * invalidé » (§8.4) - struck through, still there. An extract absent from the
 * verbatim is « rejeté sans casser le tour » (§8.1) - greyed, still there,
 * because a model that paraphrases is exactly what somebody reading this screen
 * is trying to catch. A quotation the report repeats is counted ONCE, so the
 * copies after the first say so. And an extract attributed to an objective the
 * engine did not read - settled, or belonging to another step - never became
 * matter at all, whatever its literality. Dropping any of them would leave a
 * score with no matter under it and no explanation for the hole.
 *
 * A malformed turn has no reading at all - « ne change aucun statut » (§8.1) -
 * and the layer says where the raw output is rather than drawing empty blocks.
 * Which defect it was is `turnDefect`'s answer (issue #96): « Tronqué » where
 * the provider cut the scribe at its ceiling, « Malformé » otherwise.
 */

export interface LayerObjectifsProps {
  turn: SessionTurn;
  reading: TurnReading;
  sources: ConversationSources;
  /** The farewell turn (issue #163): no scribe, so no reading at all (H81). */
  farewell: boolean;
  open: boolean;
  onToggle: () => void;
}

/** Why the extract is greyed, in the words §8.1 and §8.4 use for it. */
const DROP_LABEL: Readonly<Record<ExtractDrop, string>> = {
  absent: 'écarté : absent du verbatim',
  repeated: 'écarté : déjà compté',
  unread: 'non compté : le moteur n’a pas lu cet objectif sur ce tour',
};

/**
 * The summary line of the fold - « 2 ouverts, 1 nourri, réponse vide ».
 *
 * « Ouverts » is the ENGINE's count and not the length of the scribe's list:
 * a report naming a settled objective would otherwise announce one more open
 * objective than the step was holding, right beside a column that prints the
 * engine's own counters.
 */
function hintOf(turn: SessionTurn, reading: TurnReading): string {
  const defect = turnDefect(turn);
  // What THIS layer has to say about a defect: there was no reading. The cause
  // comes from `turnDefect` and the sentence is the layer's own (issue #96).
  if (defect !== null) return `aucune lecture, ${defect.cause}`;
  const open = reading.openObjectives;
  if (open === 0 && reading.objectives.length === 0) return 'aucun objectif ouvert sur ce tour';
  const fed = reading.objectives.filter((objective) =>
    objective.extracts.some((extract) => extract.dropped === null),
  ).length;
  const parts = [
    `${open} ouvert${open > 1 ? 's' : ''}`,
    `${fed} nourri${fed > 1 ? 's' : ''}`,
    ...(reading.emptyAnswer ? ['réponse vide'] : []),
    ...(reading.refusalDetected ? ['refus'] : []),
  ];
  return parts.join(', ');
}

export function LayerObjectifs({
  turn,
  reading,
  sources,
  farewell,
  open,
  onToggle,
}: LayerObjectifsProps) {
  const defect = turnDefect(turn);
  return (
    <TurnLayerShell
      layer="objectifs"
      hint={farewell ? FAREWELL_NOT_EVALUATED : hintOf(turn, reading)}
      open={open}
      onToggle={onToggle}
    >
      {farewell ? (
        <p>
          <b>Tour d’adieu, non évalué.</b> L’étape d’adieu n’a pas d’objectifs et l’au revoir
          n’attend pas de réponse : le scribe n’est pas appelé, aucun statut ne change (§8.1).
        </p>
      ) : defect !== null ? (
        <p>
          <b>{defect.label}.</b> {defect.note}
        </p>
      ) : (
        <>
          {reading.objectives.length === 0 ? (
            <p className="text-gray-600">
              {reading.openObjectives === 0
                ? 'Le scribe n’avait aucun objectif ouvert à juger sur ce tour.'
                : `Le scribe n’a rendu aucun objectif, alors que l’étape en tenait ${reading.openObjectives} d’ouvert.`}
            </p>
          ) : (
            reading.objectives.map((objective) => (
              <ObjectiveBlock key={objective.objectiveId} reading={objective} sources={sources} />
            ))
          )}
          {/*
            Les trois verdicts sont rendus même quand le scribe n'a nommé aucun
            objectif : c'est exactement le tour où « réponse vide » est la seule
            chose à lire, et le cacher laisserait la colonne État afficher une
            série de réponses vides que rien sur le fil n'expliquerait. « A
            répondu » vient AVANT « réponse vide » parce que c'en est la moitié
            (§8.2, H4 hybride) : un « non » sans extrait ne se comprend qu'en
            lisant d'abord que la personne a répondu.
          */}
          <dl className="flex flex-col gap-2">
            <LayerLine label="A répondu">
              <span data-testid={SESSIONS_TEST_IDS.turnAnswered} data-answered={reading.answered}>
                {reading.answered ? (
                  'oui - selon le scribe, même sans matière'
                ) : (
                  <>
                    <b>non</b> - selon le scribe, la personne n’a pas répondu à la question
                  </>
                )}
              </span>
            </LayerLine>
            <LayerLine label="Réponse vide">
              {reading.emptyAnswer ? (
                <>
                  <b>oui</b> - aucun extrait littéral attribué à un objectif ouvert, et la personne
                  n’a pas répondu (§8.2)
                </>
              ) : (
                'non - un extrait a atterri, ou la personne a répondu sans matière'
              )}
            </LayerLine>
            <LayerLine label="Refus explicite">
              {reading.refusalDetected ? (
                <>
                  <b>oui</b>
                  {reading.refusedObjective === null
                    ? ' - sans objectif visé'
                    : ` - « ${reading.refusedObjective} »`}
                </>
              ) : (
                'non'
              )}
            </LayerLine>
          </dl>
        </>
      )}
    </TurnLayerShell>
  );
}

function ObjectiveBlock({
  reading,
  sources,
}: {
  reading: ObjectiveReading;
  sources: ConversationSources;
}) {
  const changed = reading.statusBefore !== reading.statusAfter;
  // Which criterion has its frozen card open, here and not in the name itself:
  // the card is a full-width row of the grid the names sit in, so the block that
  // owns the grid is what can place it.
  const [card, setCard] = useState<string | null>(null);
  return (
    <section
      data-testid={SESSIONS_TEST_IDS.turnObjective}
      data-objective-id={reading.objectiveId}
      className="overflow-hidden rounded-md border border-gray-200"
    >
      <header className="flex flex-wrap items-center gap-2.5 bg-gray-50 px-3 py-2">
        <h4 className="font-semibold text-noir text-sm">{reading.name}</h4>
        {/*
          « obligatoire » est une propriété du protocole FIGÉ : quand la version
          jouée ne porte plus l'objectif, personne ne sait laquelle des deux
          c'était, et écrire « optionnel » serait une affirmation de plus que ce
          que le tour a enregistré.
        */}
        <span className="text-gray-600 text-xs">
          {!reading.resolved
            ? 'retiré du protocole joué'
            : reading.mandatory
              ? 'obligatoire'
              : 'optionnel'}
        </span>
        {reading.counted ? null : (
          <span className="rounded bg-jaune-tint px-2 py-0.5 font-semibold text-jaune-deep text-xs">
            non lu ce tour
          </span>
        )}
        <span
          className={`ml-auto inline-flex items-center gap-1.5 font-semibold text-xs ${
            STATUS_TEXT[reading.statusAfter]
          }`}
        >
          <span
            aria-hidden="true"
            className={`size-2.5 shrink-0 rounded-full ${STATUS_DOT[reading.statusAfter]}`}
          />
          {changed
            ? `${objectiveStatusLabel(reading.statusBefore)} → ${objectiveStatusLabel(reading.statusAfter)}`
            : objectiveStatusLabel(reading.statusAfter)}
        </span>
      </header>

      <div className="flex flex-col gap-2 px-3 py-2.5">
        {reading.extracts.length === 0 ? (
          <p className="text-gray-600 text-xs">Aucun extrait attribué ce tour.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {/*
              La clé est le RANG et non le texte : un rapport qui cite deux fois
              la même phrase rend deux lignes, dont une seule est comptée, et
              deux `<li>` de même clé se réconcilieraient l'une sur l'autre au
              sondage d'une session en cours.
            */}
            {reading.extracts.map((extract, rank) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: two extracts of one report may carry the same text, and the rank is what tells them apart
                key={rank}
                data-dropped={extract.dropped ?? ''}
                data-invalidated={extract.invalidated}
                className={`border-gray-200 border-l-2 pl-3 italic ${
                  extract.invalidated
                    ? 'text-gray-500 line-through decoration-gray-400'
                    : extract.dropped === null
                      ? 'text-gray-900'
                      : 'text-gray-500'
                }`}
              >
                {extract.text}
                {extract.invalidated ? (
                  <span className="ml-2 text-jaune-deep text-xs not-italic">invalidé</span>
                ) : null}
                {extract.dropped === null || extract.invalidated ? null : (
                  <span className="ml-2 text-jaune-deep text-xs not-italic">
                    {DROP_LABEL[extract.dropped]}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="grid items-baseline gap-x-4 gap-y-1 sm:grid-cols-[minmax(0,1fr)_auto]">
          {reading.groups.map((group) => {
            const rows = group.criteria.map((criterion) => (
              <CriterionRow
                key={criterion.criterionId}
                criterion={criterion}
                group={group}
                sources={sources}
                card={card}
                onToggleCard={(id) => setCard((was) => (was === id ? null : id))}
              />
            ));
            // A criterion that stands alone is a row of the grid, as before the
            // groups; an « l'un de » group is one framed block of rows (#181).
            if (!group.either) return rows;
            const mention = criterionGroupMention(group);
            return (
              <div
                key={group.criteria.map((criterion) => criterion.criterionId).join('+')}
                data-testid={SESSIONS_TEST_IDS.criterionGroup}
                data-or-group={group.orGroup ?? ''}
                data-tone={group.tone}
                className={`my-1 grid items-baseline gap-x-4 gap-y-1 border-l-2 py-1 pl-3 sm:col-span-2 sm:grid-cols-[minmax(0,1fr)_auto] ${GROUP_TONE_BORDER[group.tone]}`}
              >
                <p className="flex flex-wrap items-baseline gap-1.5 text-xs sm:col-span-2">
                  <span className="font-bold text-gray-600 uppercase tracking-wide">l’un de</span>
                  {mention === null ? null : (
                    <span className={`font-semibold ${CRITERION_TONE_TEXT[group.tone]}`}>
                      {mention}
                    </span>
                  )}
                </p>
                {rows.flatMap((row, rank) =>
                  rank === 0
                    ? [row]
                    : [
                        <p
                          // biome-ignore lint/suspicious/noArrayIndexKey: the separators are positional, one between two neighbouring rows
                          key={`or-${rank}`}
                          className="font-bold text-gray-500 text-xs uppercase tracking-wide sm:col-span-2"
                        >
                          ou
                        </p>,
                        row,
                      ],
                )}
              </div>
            );
          })}
        </div>

        {/*
          La quantité COMPTÉE est toujours lisible - elle sort des extraits de
          l'instantané - mais « atteinte » se lit contre une attente, et le
          protocole figé est le seul à la porter. Quand il ne résout plus
          l'objectif, la ligne le dit au lieu de la déclarer atteinte, ce qui
          ferait passer pour un verdict une attente que personne ne connaît.
        */}
        <p
          className={`text-xs ${reading.quantityReached === false ? 'text-rouge-deep' : 'text-gray-600'}`}
        >
          Quantité : {reading.quantity.sentences} phrase
          {reading.quantity.sentences > 1 ? 's' : ''} · {reading.quantity.chars} caractère
          {reading.quantity.chars > 1 ? 's' : ''}
          {reading.expected === null
            ? ' · attendu inconnu : le protocole joué ne porte plus cet objectif'
            : reading.expected.minSentences === null && reading.expected.minChars === null
              ? ' · aucune quantité attendue'
              : ` · attendu ${[
                  reading.expected.minSentences === null
                    ? null
                    : `${reading.expected.minSentences} phrases`,
                  reading.expected.minChars === null
                    ? null
                    : `${reading.expected.minChars} caractères`,
                ]
                  .filter((part): part is string => part !== null)
                  .join(' et ')}${reading.quantityReached === false ? ' - insuffisant' : ''}`}
        </p>

        {reading.refused ? (
          <p className="text-jaune-deep text-xs">
            Refus explicite visant cet objectif : il passe à raté (§7.3).
          </p>
        ) : null}
      </div>
    </section>
  );
}

/**
 * One criterion of an objective: its name (which opens the frozen card), its
 * score against its bar, the tick of the code, and the scribe's justification.
 *
 * Inside an « l'un de » group (#181) a criterion that CARRIED the group wears a
 * green pill, and one that did not - when the group is filled by another - is
 * softened and says so: it keeps its figure, its red tick and its justification
 * (the reading is the scribe's, and shown whole), but it no longer holds the
 * objective back.
 */
function CriterionRow({
  criterion,
  group,
  sources,
  card,
  onToggleCard,
}: {
  criterion: CriterionReading;
  group: CriterionGroupReading;
  sources: ConversationSources;
  card: string | null;
  onToggleCard: (criterionId: string) => void;
}) {
  const played = sources.criteria.get(criterion.criterionId);
  const bar = thresholdLabel(criterion.threshold, criterion.plancher);
  const passable = passableLabel(criterion.passable);
  const tone = criterionTone(criterion);
  const carrier = group.either && group.tone === 'met' && group.carriers.includes(criterion);
  const spare = group.either && group.tone === 'met' && !carrier;
  // Softened and not hidden: the scribe's reading of the criterion is shown
  // whole, only its weight on the objective is gone.
  const dim = spare ? 'opacity-60' : '';
  return (
    <div className="contents" data-carrier={carrier} data-spare={spare}>
      <span className={`min-w-0 font-semibold text-gray-900 ${dim}`}>
        {played === undefined ? (
          criterion.name
        ) : (
          <CriterionName
            criterion={played}
            open={card === criterion.criterionId}
            onToggle={() => onToggleCard(criterion.criterionId)}
            testId={SESSIONS_TEST_IDS.criterion}
          />
        )}
        {carrier ? (
          <span
            data-testid={SESSIONS_TEST_IDS.criterionCarrier}
            className="ml-2 inline-flex rounded-pill bg-vert-tint px-2 font-semibold text-vert-deep text-xs"
          >
            a porté le groupe
          </span>
        ) : null}
        {spare ? (
          <span className="ml-2 font-normal text-gray-500 text-xs">
            non requis : {group.carriers.map((one) => one.name).join(' et ')}{' '}
            {group.carriers.length > 1 ? 'ont' : 'a'} suffi
          </span>
        ) : null}
      </span>
      {/*
        The two FORMS of §8.2, told apart by the plancher: a scored
        criterion reads « 62 · tranche 50-75 » beside « seuil 75 » or
        « plancher 50 », an unscored one reads « rempli » and carries
        no bar at all - there is none to show, and a « seuil » beside
        a verdict would name a comparison nobody made.
      */}
      <span className={`inline-flex items-baseline gap-1.5 tabular-nums ${dim}`}>
        <b className={`font-bold text-base ${CRITERION_TONE_TEXT[tone]}`}>
          {isVerdictReading(criterion)
            ? verdictLabel(criterion.verdict)
            : scoreLabel(criterion.score, criterion.band)}
        </b>
        {bar === null ? null : <span className="text-gray-600 text-xs">{bar}</span>}
        {/*
          The bound that makes a miss PASSABLE, named beside the bar
          it falls short of (#110): the yellow says « close », this
          says from where - colour alone is never the only carrier.
        */}
        {passable === null ? null : <span className="text-gray-600 text-xs">{passable}</span>}
        {tone === 'unjudged' ? null : (
          <span className={`font-bold ${CRITERION_TONE_TEXT[tone]}`}>
            {tone === 'met' ? '✓' : '✗'}
          </span>
        )}
      </span>
      {hasIgnoredThreshold(criterion) ? (
        <span
          data-testid={SESSIONS_TEST_IDS.criterionThresholdIgnored}
          className="inline-flex items-center gap-1.5 text-jaune-deep text-xs sm:col-span-2"
        >
          <TriangleAlert aria-hidden="true" className="size-3.5 shrink-0" />
          {IGNORED_THRESHOLD_SENTENCE}
        </span>
      ) : null}
      {criterion.justification === '' ? null : (
        <span className={`text-gray-600 sm:col-span-2 ${dim}`}>{criterion.justification}</span>
      )}
      {played !== undefined && card === criterion.criterionId ? (
        <CriterionCard criterion={played} className="sm:col-span-2" />
      ) : null}
    </div>
  );
}
